import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Bell, MessageCircle, User, AlertCircle } from 'lucide-react';
import { lerToken, sessaoVencida, tokenParaConectar } from '@/lib/sessao';
import { useAuth } from '@/context/AuthContext';
import { useBrand } from '@/context/BrandContext';

// LOTE 204 (S1): o alerta do SDR de uma conta aparecia em outra. O provider montava UMA vez acima das rotas e só
// reconectava pelo evento `storage`, que não dispara na mesma aba: depois de sair e entrar com outra conta, o stream
// seguia aberto com o token antigo e o contador antigo ficava na tela. Agora a sessão (usuário, conta e marca ativa) é a
// chave: mudou, tudo zera e reconecta, e toda chamada leva a marca ativa (X-Brand-Id), que o servidor valida.
/** Os cabeçalhos de toda chamada: o token e a marca ativa. */
export const cabecalhosDaChamada = (token, marca, extra = {}) => ({ Authorization: `Bearer ${token}`,
  ...(marca ? { 'X-Brand-Id': marca } : {}), ...extra });

export const chaveDaSessao = (user, account, marca) =>
  (user?.id ? `${user.id}|${account?.id || user.account_id || ''}|${marca || ''}` : '');

const NotificationContext = createContext(null);

// Sound para notificações urgentes
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 880; // A5
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (e) {
    console.log('Audio notification not supported');
  }
};

export const NotificationProvider = ({ children }) => {
  const { user, account } = useAuth() || {};
  const { activeBrand } = useBrand() || {};
  const marcaAtiva = activeBrand?.id || '';
  const marcaRef = useRef(marcaAtiva);
  marcaRef.current = marcaAtiva;
  const chave = chaveDaSessao(user, account, marcaAtiva);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sdrWaitingCount, setSdrWaitingCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  // `eventSourceRef` SAIU em 10/09/2026: ele nunca recebia valor no caminho por
  // `fetch`, e o cleanup o "fechava" sem fechar nada — nao havia o que abortar.
  // Quem encerra a cadeia agora e o AbortController, junto com a geracao.
  const reconnectTimeoutRef = useRef(null);
  const abortRef = useRef(null);
  // A GERACAO DA CADEIA. Cada `connectSSE` abre uma nova e invalida a anterior;
  // o callback de uma cadeia velha — o catch de um fetch abortado, um timer que
  // escapou — confere a geracao e desiste. Foi a falta disto que deixou seis
  // cadeias rodando ao mesmo tempo em 10/09.
  const geracaoRef = useRef(0);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Conectar ao stream SSE
  // Encerra a cadeia em curso: invalida a geracao, aborta o fetch em voo e
  // cancela o timer pendente. Usado ao desmontar, quando a sessao sai em outra
  // aba, e antes de abrir uma cadeia nova.
  const pararSSE = useCallback(() => {
    geracaoRef.current += 1;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const connectSSE = useCallback(() => {
    // UMA CADEIA POR VEZ: quem chama aqui encerra a anterior antes de abrir outra.
    pararSSE();
    const geracao = geracaoRef.current;
    const vigente = () => geracao === geracaoRef.current;

    // Sem token nao ha o que conectar; vencido, a sessao ja foi encerrada por
    // `tokenParaConectar`. Nos dois casos, nenhuma requisicao sai daqui.
    const token = tokenParaConectar('stream');
    if (!token) return;

    const controle = new AbortController();
    abortRef.current = controle;

    try {
      // EventSource não suporta headers customizados, então usamos fetch com stream
      const fetchSSE = async () => {
        try {
          const response = await fetch(`${API}/api/notifications/stream`, {
            headers: cabecalhosDaChamada(token, marcaRef.current, { Accept: 'text/event-stream' }),
            signal: controle.signal,
          });

          // 401 E FIM DE SESSAO, e nao falha de rede: nao reagenda. Ate 10/09 o
          // status era descartado aqui, e o 401 virava "tenta de novo em 5 s"
          // para sempre. So encerra se esta cadeia ainda vale e o token recusado
          // e o atual — um 401 de antes de um novo login nao diz nada da sessao nova.
          if (response.status === 401) {
            if (vigente()) {
              setIsConnected(false);
              if (token === lerToken()) sessaoVencida('stream');
            }
            return;
          }

          if (!response.ok) {
            throw new Error('SSE connection failed');
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          if (vigente()) setIsConnected(true);

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            const text = decoder.decode(value);
            const lines = text.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  handleNotification(data);
                } catch (e) {
                  // Ignore parse errors for heartbeats
                }
              }
            }
          }
        } catch (error) {
          // Cadeia encerrada de proposito — desmontagem, sessao encerrada ou uma
          // cadeia nova no lugar: nao e erro, e reagendar aqui criaria a orfa.
          if (controle.signal.aborted || !vigente()) return;
          console.log('SSE Error:', error);
          setIsConnected(false);

          // Reconectar apos 5 segundos — mas antes, a mesma pergunta da conexao:
          // token vencido encerra a sessao aqui, e sem token nao ha o que reabrir.
          // O intervalo fixo continua; backoff e a divida do item 7.
          if (!tokenParaConectar('stream')) return;
          reconnectTimeoutRef.current = setTimeout(() => {
            if (vigente()) connectSSE();
          }, 5000);
        }
      };

      fetchSSE();
    } catch (error) {
      console.error('Error connecting to SSE:', error);
    }
  }, [API, pararSSE]);

  // Processar notificação recebida
  const handleNotification = useCallback((data) => {
    if (data.type === 'heartbeat' || data.type === 'connected') {
      return;
    }
    // O servidor já filtra por conta e marca; aqui, a mesma conferência de novo (nada de outra marca chega à tela).
    if (data.brand_id && data.brand_id !== marcaRef.current) return;

    // Adicionar à lista de notificações
    setNotifications(prev => [data, ...prev.slice(0, 49)]);
    setUnreadCount(prev => prev + 1);

    // Mostrar toast baseado no tipo
    if (data.type === 'sdr_lead_waiting') {
      // Notificação especial para leads aguardando
      playNotificationSound();
      
      toast.custom((t) => (
        <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-4 rounded-lg shadow-lg max-w-md animate-in slide-in-from-top">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white/20 rounded-full">
              <User className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-semibold">{data.title}</p>
              <p className="text-sm opacity-90">{data.message}</p>
              {data.data?.channel && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                    {data.data.channel === 'whatsapp' ? '📱 WhatsApp' : '📸 Instagram'}
                  </span>
                  {data.data.qualification_score > 0 && (
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                      Score: {data.data.qualification_score}%
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ), {
        duration: 10000,
        position: 'top-right'
      });

      // Atualizar contador de SDR
      setSdrWaitingCount(prev => prev + 1);
    } else if (data.priority === 'urgent') {
      playNotificationSound();
      toast.error(data.message, {
        description: data.title,
        duration: 8000
      });
    } else if (data.priority === 'high') {
      toast.warning(data.message, {
        description: data.title,
        duration: 6000
      });
    } else {
      toast.info(data.message, {
        description: data.title,
        duration: 4000
      });
    }
  }, []);

  // Buscar contagem inicial
  const fetchUnreadCount = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API}/api/notifications/unread-count`, {
        headers: cabecalhosDaChamada(token, marcaRef.current)
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
        setSdrWaitingCount(data.sdr_waiting_count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [API]);

  // Buscar notificações
  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API}/api/notifications?limit=20`, {
        headers: cabecalhosDaChamada(token, marcaRef.current)
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [API]);

  // Marcar como lida
  const markAsRead = useCallback(async (notificationId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await fetch(`${API}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: cabecalhosDaChamada(token, marcaRef.current)
      });

      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [API]);

  // Marcar todas como lidas
  const markAllAsRead = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await fetch(`${API}/api/notifications/mark-all-read`, {
        method: 'PUT',
        headers: cabecalhosDaChamada(token, marcaRef.current)
      });

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [API]);

  // A SESSÃO É A CHAVE (S1): ao montar e a cada troca de usuário, conta ou marca, tudo zera e reconecta.
  useEffect(() => {
    pararSSE();
    setIsConnected(false);
    setNotifications([]);
    setUnreadCount(0);
    setSdrWaitingCount(0);
    const token = localStorage.getItem('token');
    // O usuário da revenda (PROMPT 215) não tem as notificações da plataforma: os alertas dele estão no portal.
    if (token && chave && user?.role !== 'revenda') {
      fetchUnreadCount();
      fetchNotifications();
      connectSSE();
    }
    return () => {
      pararSSE();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  // Reconectar quando o usuário faz login
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          fetchUnreadCount();
          fetchNotifications();
          connectSSE();
        } else {
          // A sessao saiu em outra aba: a cadeia desta nao pode seguir aberta.
          pararSSE();
          setIsConnected(false);
          setNotifications([]);
          setUnreadCount(0);
          setSdrWaitingCount(0);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connectSSE, fetchUnreadCount, fetchNotifications, pararSSE]);

  const value = {
    notifications,
    unreadCount,
    sdrWaitingCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    fetchUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    // Retornar valores padrão se o contexto não estiver disponível
    return {
      notifications: [],
      unreadCount: 0,
      sdrWaitingCount: 0,
      isConnected: false,
      markAsRead: () => {},
      markAllAsRead: () => {},
      fetchNotifications: () => {},
      fetchUnreadCount: () => {}
    };
  }
  return context;
};

export default NotificationProvider;
